import { useEffect, useMemo, useState } from "react";
import { Checkbox, Input, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";

import styles from "./stylesRefundRequestModal.module.css";

import SaveButton from "../../../CommonComponents/SaveButton/SaveButton";
import { useWithdrawalReview } from "../WithdrawalReviewContext";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { getApiSuccessMessage } from "../../../../utils/ErrorUtils";

const MONEY_PATTERN = /^\d+(?:\.\d{1,2})?$/;

export function getRefundAmountError(value, withdrawalAmount) {
  const amountText = value.trim();
  if (!MONEY_PATTERN.test(amountText) || Number(amountText) <= 0) {
    return AppStrings.WITHDRAWAL_REFUND_AMOUNT_INVALID;
  }
  const withdrawalCap = Number(withdrawalAmount);
  if (!Number.isFinite(withdrawalCap) || withdrawalCap <= 0) {
    return AppStrings.WITHDRAWAL_REFUND_AMOUNT_UNAVAILABLE;
  }
  if (Number(amountText) > withdrawalCap) {
    return AppStrings.WITHDRAWAL_REFUND_AMOUNT_EXCEEDS_WITHDRAWAL;
  }
  return "";
}

function RefundRequestModal({ currencySymbol }) {
  const {
    isRefundModalOpen,
    setIsRefundModalOpen,
    handleRefund,
    recordRefunded,
    refundTransactionLoading,
  } = useWithdrawalReview();
  const [refundAmount, setRefundAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [disclaimerAcknowledged, setDisclaimerAcknowledged] = useState(false);
  const [amountTouched, setAmountTouched] = useState(false);
  const [notesTouched, setNotesTouched] = useState(false);

  const amountError = useMemo(
    () =>
      getRefundAmountError(
        refundAmount,
        recordRefunded?.withdrawal_amount,
      ),
    [refundAmount, recordRefunded?.withdrawal_amount],
  );
  const notesError = notes.trim()
    ? ""
    : AppStrings.WITHDRAWAL_REFUND_NOTES_REQUIRED;
  const isValid =
    !amountError && !notesError && disclaimerAcknowledged && recordRefunded;

  useEffect(() => {
    if (isRefundModalOpen) {
      setRefundAmount("");
      setNotes("");
      setDisclaimerAcknowledged(false);
      setAmountTouched(false);
      setNotesTouched(false);
    }
  }, [isRefundModalOpen]);

  const handleClose = () => {
    setIsRefundModalOpen(false);
  };

  const handleSave = async () => {
    setAmountTouched(true);
    setNotesTouched(true);
    if (!isValid) return;

    try {
      message.info(AppStrings.REQUEST_BEING_PROCESSED);
      const response = await handleRefund({
        user_id: recordRefunded.user_id,
        transaction_id: recordRefunded.transaction_id,
        refund_amount: Number(refundAmount),
        notes: notes.trim(),
        disclaimer_acknowledged: true,
      });
      message.success(
        getApiSuccessMessage(
          response,
          AppStrings.WITHDRAWAL_REFUND_SUCCESS,
        ),
      );
    } catch (error) {
      // Error notification is handled by the withdrawal review context.
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>{AppStrings.WITHDRAWAL_REFUND_MODAL_TITLE}</h2>
        <CloseOutlined onClick={handleClose} className={styles.closeIcon} />
      </div>

      <div className={styles.divider} />

      <div className={styles.content}>
        <label className={styles.field}>
          <span>{AppStrings.WITHDRAWAL_REFUND_AMOUNT}</span>
          <Input
            aria-label={AppStrings.WITHDRAWAL_REFUND_AMOUNT}
            inputMode="decimal"
            prefix={currencySymbol}
            value={refundAmount}
            placeholder={AppStrings.WITHDRAWAL_REFUND_AMOUNT_PLACEHOLDER}
            onChange={(event) => setRefundAmount(event.target.value)}
            onBlur={() => setAmountTouched(true)}
          />
          {amountTouched && amountError && (
            <span className={styles.errorMessage}>{amountError}</span>
          )}
        </label>

        <label className={styles.field}>
          <span>{AppStrings.WITHDRAWAL_REFUND_NOTES}</span>
          <Input.TextArea
            aria-label={AppStrings.WITHDRAWAL_REFUND_NOTES}
            value={notes}
            placeholder={AppStrings.WITHDRAWAL_REFUND_NOTES_PLACEHOLDER}
            onChange={(event) => setNotes(event.target.value)}
            onBlur={() => setNotesTouched(true)}
            rows={3}
          />
          {notesTouched && notesError && (
            <span className={styles.errorMessage}>{notesError}</span>
          )}
        </label>

        <Checkbox
          checked={disclaimerAcknowledged}
          onChange={(event) =>
            setDisclaimerAcknowledged(event.target.checked)
          }
        >
          {AppStrings.WITHDRAWAL_REFUND_DISCLAIMER}
        </Checkbox>
      </div>

      <div className={styles.footer}>
        <SaveButton
          cancelButton
          cancelButtonText={AppStrings.CLOSE}
          buttonText={AppStrings.WITHDRAWAL_REFUND_ACTION}
          callbackFunction={handleSave}
          callbackFunctionCancel={handleClose}
          isDisabled={!isValid}
          isApiRunning={refundTransactionLoading}
        />
      </div>
    </div>
  );
}

export default RefundRequestModal;
